import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router';
import { NavigationFunction as RootNavigation, Pages } from "../../../../Navigation/root"
import { colors, fonts, frames, glyphs, useMediaQuery } from "../../../../Styles";
import { PageHeader, NewSelect, ArchivedReportCard, NewInput } from "../../../../Components";
import { ButtonTypes, NewFieldStyles, NewInputHandles, SelectFieldOption_Values } from "../../../../Definitions";

// TODO testdata
import CardsView from "../../../../Assets/TestData/cardsView";
import { useAppSelector } from "../../../../Store";
import { Graph_QL } from "../../../../Services";
import { useReport } from "../../../../Services/Agrisys_GqlDataCaller/ReportDataCalls/useReport";
import NewButton from "../../../../Components/BaseComponents/newButton";

// #region | FilterSelect
const FilterSelect = memo(({ selectedOption, onSelectOption, searchRef, onSearchChange, onResetSearch }: {
  selectedOption: SelectFieldOption_Values | undefined;
  onSelectOption: (selectedOption: SelectFieldOption_Values | undefined) => void;
  searchRef: React.RefObject<NewInputHandles> | undefined;
  onSearchChange: ((e: string) => void) | undefined;
  onResetSearch: () => void;
}) => {
  const { t } = useTranslation();
  const smallSize = useMediaQuery(`(min-width: 550px) and (max-width: 1150px)`);
  const popperSize = useMediaQuery(`(max-width: 550px)`);
  const listRef = useRef<HTMLDivElement>(null);
  const buttonListRef = useRef<HTMLDivElement>(null);
  const [showPopper, setShowPopper] = useState<boolean>(false)
  const [searchValueState, setSearchValueState] = useState("");

  const handleSearchChange = (value: string) => {
    setSearchValueState(value);
    onSearchChange?.(value);
  };

  const resetSearch = () => {
    searchRef?.current?.setValue("");
    setSearchValueState("");
    onResetSearch();
  };
  
  const hasSearchValue = searchValueState.length > 0;
  
  const filterOptions = [
    { label: t("reportPages.archive.filterByNumber", {defaultValue: "Report Number"}), id: 0 },
    { label: t("reportPages.archive.filterByName", {defaultValue: "Report Name Alphabetical"}), id: 1 },
  ];

  let _fieldWidth = smallSize ? "140px" : "280px";
  let _fieldStyle = popperSize ? NewFieldStyles.greyDarkSecondary : NewFieldStyles.greyDarkPrimary;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        listRef.current && 
        !listRef.current.contains(event.target as Node) && 
        buttonListRef.current && 
        !buttonListRef.current.contains(event.target as Node)
      ) {
        setShowPopper(false);
      }
    };

    const handleEnterKey = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        setShowPopper(false);
      }
    };

    if (showPopper) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener("keydown", handleEnterKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener("keydown", handleEnterKey);
    };
  }, [showPopper]);

  const filterSearchList = (
    <div style={{ display: "flex", flexDirection: popperSize ? "column" : "row", gap: "10px" }}>
      <NewSelect
        placeholder={t("filterBy", { defaultValue: "Filter By" })}
        options={filterOptions}
        onChange={onSelectOption}
        fieldOverrides={{
          fieldStyle: _fieldStyle,
          fieldWidth: _fieldWidth
        }}
        valueOverrides={{
          defaultValue: selectedOption ?? filterOptions[0]
        }}
      />
      <NewInput
        ref={searchRef}
        startEnhancer={glyphs.Search({})}
        onChange={handleSearchChange}
        fieldOverrides={{
          fieldStyle: _fieldStyle,
          fieldWidth: _fieldWidth
        }}
        isClearable
        placeholder={t("search", { defaultValue: "search" })}
      />
    </div>
  )

  const popperComponent = <div style={{ position: "relative" }}>
    <div style={{display: "flex", gap: "10px"}}>
      { hasSearchValue && <NewButton content={"Reset"}
        onClick={resetSearch}
        buttonType={ButtonTypes.textButton}
        overrides={{baseStyles: {height: "25px"}, contentStyles: {...fonts.fontSize3}}}
      /> }
      <NewButton ref={buttonListRef} content={""}
        onClick={() => setShowPopper( showPopper ? false : true)}
        buttonType={ButtonTypes.glyphButton}
        startEnhancer={ showPopper ? glyphs.Close({ size: "25px" }) : glyphs.Setting({ size: "25px" })}
      />
    </div>
    <div ref={listRef} style={{ position: "absolute", top: "30px", right: "0px", zIndex: 99999999, backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px" + colors.blackPrimary + "4d", display: showPopper ? "block" : "none" }}>
      {filterSearchList}
    </div>
  </div>
  
  if (!popperSize) return filterSearchList;
  return popperComponent;
});
// #endregion

export default function Archive() {
  const {t} = useTranslation()
  const navigator = useNavigate()
  const [, setShowReport] = useState<number | undefined>(undefined)
  
  const searchRef = useRef<NewInputHandles>(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // #region | GraphQl
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  
  const {call: getArchivedReport, data: archivedReportState} = useReport(connection);

  const archivedReports = useMemo(() => {
    if (archivedReportState && archivedReportState[domain]) {
      return archivedReportState[domain].data;
    }
    return [];
  },[archivedReportState, domain])

  useEffect(() => {
    getArchivedReport({fetchAmount: 50, offset: 0, ifFetchData: false});
  }, [])
  // #endregion
  
  // #region | Handle selected filter option and sorted reports
  const [selectedFilterOption, setSelectedFilterOption] = useState<SelectFieldOption_Values | undefined>();
  
  const handleSelectOption = useCallback((selectedOption: SelectFieldOption_Values | undefined) => {
    setSelectedFilterOption(selectedOption)
  }, []);

  const filteredReports = useMemo(() => {
    const data = [...archivedReports];
  
    // Sortér
    if (selectedFilterOption?.id === 1) {
      data.sort((a, b) => (b.reportId ?? 0) - (a.reportId ?? 0));
    } else {
      data.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    }
  
    // Filtrér
    if (searchTerm.trim()) {
      return data.filter(
        (r) =>
          r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          String(r.reportId)?.includes(searchTerm)
      );
    }
  
    return data;
  }, [archivedReports, selectedFilterOption?.id, searchTerm]);
  // #endregion
  
  // Handle report click
  const onReportSelect = (id: number) => {
    setShowReport(id);
    
    const selectedArchivedReportData = archivedReports.find(report => report.reportId === id);
    if (selectedArchivedReportData) {
      RootNavigation({ Navigator: navigator, navigation_page: Pages.reports, navigation_tab: Pages.report, replace: true, navArgs: { selected: selectedArchivedReportData?.reportId }, currentDomain: farm });
    }
  };

  return (
    <frames.Page_inner>
      <PageHeader
        topHeader={t(`menuHeaders.${Pages.reports}`, { defaultValue: "Reports" })}
        subHeader={t(`menuHeaders.${Pages.archive}`, { defaultValue: "Archive" })}
        subContent={ <FilterSelect
          onSelectOption={handleSelectOption}
          selectedOption={selectedFilterOption}
          searchRef={searchRef}
          onSearchChange={(value: string) => setSearchTerm(value)}
          onResetSearch={() => setSearchTerm("")}
        /> }
      />
          
      <frames._page_inner_content_scroll>
        <CardsView
          items={filteredReports.map((item, index) => (
            <ArchivedReportCard key={index} report={item} onCardSelect={onReportSelect} />
          ))}
        />
      </frames._page_inner_content_scroll>
    </frames.Page_inner>
  )
}